import { useState } from 'react';
import { Link } from 'react-router-dom';
import { latestAddresses, backupAddresses } from '@/mocks/addresses';
import { faqs, articles, categories } from '@/mocks/content';
import PageSEO from '@/components/base/PageSEO';
import { FAQPageSchema } from '@/components/base/SchemaOrg';

function AddressCard({ address, isPrimary = false }: { address: typeof latestAddresses[0]; isPrimary?: boolean }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(address.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <div className={`rounded-xl p-5 transition-all duration-300 ${isPrimary ? 'bg-primary-500 text-white' : 'bg-background-50 border border-background-200/70 hover:border-primary-200'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isPrimary ? 'bg-white/20 text-white' : 'bg-accent-100 text-accent-800'}`}>
            {address.label}
          </span>
          <span className={`inline-flex items-center gap-1 text-xs ${isPrimary ? 'text-white/80' : 'text-foreground-500'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${address.status === 'active' ? 'bg-green-400' : 'bg-yellow-400'}`}></span>
            {address.status === 'active' ? '正常' : '待命'}
          </span>
        </div>
        <span className={`text-xs ${isPrimary ? 'text-white/70' : 'text-foreground-400'}`}>更新于 {address.updatedAt}</span>
      </div>
      <div className={`flex items-center justify-between p-3 rounded-lg mb-3 ${isPrimary ? 'bg-white/10' : 'bg-secondary-100'}`}>
        <span className={`text-sm font-medium truncate mr-2 ${isPrimary ? 'text-white' : 'text-foreground-900'}`}>{address.url}</span>
        <button
          onClick={handleCopy}
          className={`flex-shrink-0 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${isPrimary ? 'bg-white text-primary-600 hover:bg-white/90' : 'bg-primary-500 text-white hover:bg-primary-600'}`}
        >
          {copied ? '已复制' : '复制地址'}
        </button>
      </div>
      <div className="flex items-center gap-4">
        <span className={`text-xs ${isPrimary ? 'text-white/70' : 'text-foreground-500'}`}>
          <i className="ri-speed-up-line mr-1"></i>{address.speed}
        </span>
        <span className={`text-xs ${isPrimary ? 'text-white/70' : 'text-foreground-500'}`}>
          <i className="ri-earth-line mr-1"></i>{address.region}
        </span>
      </div>
      <p className={`mt-2 text-xs ${isPrimary ? 'text-white/60' : 'text-foreground-400'}`}>{address.description}</p>
    </div>
  );
}

function BackupAddressItem({ addr }: { addr: typeof backupAddresses[0] }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(addr.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <div className="flex items-center justify-between bg-background-50 rounded-lg p-3">
      <div>
        <span className="text-sm text-foreground-700">{addr.label}</span>
        <span className="block text-xs text-foreground-400 mt-0.5">{addr.url}</span>
      </div>
      <button
        onClick={handleCopy}
        className="flex-shrink-0 px-3 py-1.5 bg-secondary-500 text-white rounded-md text-xs font-medium hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap ml-2"
      >
        {copied ? '已复制' : '复制'}
      </button>
    </div>
  );
}

export default function HomePage() {
  const displayedFaqs = faqs.slice(0, 6);
  const featuredArticles = articles.slice(0, 4);

  return (
    <>
      <PageSEO
        title="海角最新地址·官方入口实时更新 - 海角最新地址更新入口与访问指南"
        description="找不到海角最新地址？本页提供海角最新地址的实时更新入口与访问指南，收藏本页即可第一时间获取海角最新地址，不再因地址失效而烦恼，快速稳定直达。海角最新地址入口、海角最新地址更新、海角地址失效解决方案。"
        keywords="海角最新地址,海角最新地址入口,海角最新地址更新,海角地址失效解决,海角官方地址,海角访问入口,海角备用地址"
        canonicalUrl="https://haijiao.readdy.ai/"
      >
        <FAQPageSchema questions={faqs} />
      </PageSEO>

      {/* Hero Section */}
      <section className="relative w-full pt-16 pb-20 md:pt-20 md:pb-28">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20golden%20abstract%20geometric%20ocean%20waves%20with%20soft%20gradient%20background%2C%20minimalist%20modern%20design%2C%20elegant%20flowing%20curves%20in%20warm%20coral%20and%20amber%20tones%2C%20clean%20light%20background%20with%20subtle%20texture%2C%20professional%20corporate%20aesthetic%2C%20no%20text&width=1600&height=600&seq=hero-haijiao-home&orientation=landscape"
            alt="海角最新地址背景"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-background-50"></div>
        </div>
        <div className="relative w-full px-4 md:px-6 max-w-5xl mx-auto text-center">
          <h1 className="font-heading text-3xl md:text-5xl text-white mb-4">
            海角最新地址·官方入口实时更新
          </h1>
          <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            找不到海角最新地址？收藏本页即可第一时间获取海角最新地址，不再因地址失效而烦恼，快速稳定直达。
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#address-section"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-down-line"></i>
              查看最新地址
            </a>
            <Link
              to="/category/faq"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-full font-medium text-sm hover:bg-white/30 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-answer-line"></i>
              常见问题
            </Link>
          </div>
        </div>
      </section>

      {/* Address Section */}
      <section id="address-section" className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              海角最新地址列表
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              以下为当前有效的海角官方地址，建议收藏本页以便随时获取最新地址更新
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {latestAddresses.map((addr, index) => (
              <AddressCard key={addr.id} address={addr} isPrimary={index === 0} />
            ))}
          </div>

          {backupAddresses.length > 0 && (
            <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-5">
              <h3 className="font-heading text-lg text-accent-800 mb-3 flex items-center gap-2">
                <i className="ri-archive-line"></i>备用地址
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {backupAddresses.map((addr) => (
                  <BackupAddressItem key={addr.id} addr={addr} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Category Quick Links */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              快速导航
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto">
              选择您需要的栏目，获取相关帮助与信息
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group bg-background-50 rounded-xl p-5 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center mb-3 group-hover:bg-accent-200 transition-colors">
                  <i className={`${cat.icon} text-lg text-accent-600`}></i>
                </div>
                <h3 className="font-heading text-base text-foreground-900 mb-1">{cat.name}</h3>
                <p className="text-xs text-foreground-500 mb-2">{cat.description}</p>
                <span className="text-xs text-primary-500 font-medium">{cat.articleCount} 篇文章</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                精选文章
              </h2>
              <p className="text-sm text-foreground-500">最新发布的海角地址相关文章</p>
            </div>
            <Link
              to="/category/update-news"
              className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featuredArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <span className="inline-block px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full mb-2">
                    {categories.find(c => c.slug === article.category)?.name || article.category}
                  </span>
                  <h3 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-3">{article.excerpt}</p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishedAt}</span>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500">
              关于海角地址的常见问题解答
            </p>
          </div>
          <div className="space-y-3">
            {displayedFaqs.map((faq, index) => (
              <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                  <h3 className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</h3>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-secondary-100 flex items-center justify-center group-open:bg-primary-100 transition-colors">
                    <i className="ri-add-line text-sm text-foreground-600 group-open:hidden"></i>
                    <i className="ri-subtract-line text-sm text-primary-500 hidden group-open:block"></i>
                  </span>
                </summary>
                <div className="px-5 pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/category/faq"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              查看更多问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
            收藏本站，随时获取海角最新地址
          </h2>
          <p className="text-sm text-foreground-500 mb-8 max-w-lg mx-auto">
            将本页面加入浏览器收藏夹，即可第一时间获取海角最新地址更新，不再因地址失效而烦恼。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => {
                if (navigator.userAgent.includes('Mac')) {
                  alert('请按 Command+D 将本页加入收藏夹');
                } else {
                  alert('请按 Ctrl+D 将本页加入收藏夹');
                }
              }}
              className="inline-flex items-center gap-2 px-8 py-3 bg-accent-500 text-white rounded-full font-medium text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-bookmark-line"></i>
              立即收藏本站
            </button>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-8 py-3 border-2 border-primary-500 text-primary-500 rounded-full font-medium text-sm hover:bg-primary-500 hover:text-white transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-information-line"></i>
              了解更多
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}